import { useEffect, useState } from "react";
import { api, rupee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const Stat = ({ label, value, sub }) => (
  <div className="admin-surface p-6">
    <p className="text-[10px] uppercase tracking-[0.25em] text-[#8C867D]">{label}</p>
    <p className="text-2xl font-serif mt-2 text-[#F4F4F5]">{value}</p>
    {sub && <p className="text-xs text-[#C5A059] mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  const { hasPerm } = useAuth();
  const [ship, setShip] = useState({ shipping_fee: 250, free_shipping_threshold: 15000 });
  const [savingShip, setSavingShip] = useState(false);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
    api.get("/settings/shipping").then((r) => setShip(r.data)).catch(() => {});
  }, [api]);
  const saveShip = async () => {
    setSavingShip(true);
    try {
      const { data } = await api.put("/admin/settings", { shipping_fee: +ship.shipping_fee, free_shipping_threshold: +ship.free_shipping_threshold });
      setShip(data);
      toast.success("Delivery charges updated — live at checkout");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSavingShip(false); }
  };
  if (!s) return <p>Loading…</p>;
  return (
    <div>
      <h1 className="font-serif text-3xl text-[#F4F4F5]">Dashboard</h1>
      <div className="grid md:grid-cols-4 gap-4 mt-6">
        <Stat label="Total Sales" value={rupee(s.total_sales)} sub={`Today: ${rupee(s.today_sales)}`} />
        <Stat label="Orders" value={s.total_orders} sub={`Pending: ${s.pending_orders}`} />
        <Stat label="Products" value={s.total_products} sub={`Low stock: ${s.low_stock}`} />
        <Stat label="Customers" value={s.total_customers} sub={`Staff: ${s.total_staff}`} />
      </div>
      <div className="admin-surface p-6 mt-6">
        <p className="eyebrow mb-4">Revenue (₹)</p>
        <div style={{ height: 260 }}>
          <ResponsiveContainer><LineChart data={s.chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis dataKey="date" stroke="#8C867D" fontSize={11} />
            <YAxis stroke="#8C867D" fontSize={11} />
            <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A" }} />
            <Line type="monotone" dataKey="revenue" stroke="#C5A059" strokeWidth={2} dot={false} />
          </LineChart></ResponsiveContainer>
        </div>
      </div>
      <div className="admin-surface p-6 mt-6">
        <p className="eyebrow mb-4">Recent Orders</p>        <table className="w-full text-sm">
          <thead className="text-[#8C867D] text-left text-xs uppercase tracking-widest">
            <tr><th className="py-2">Order</th><th>Customer</th><th>Total</th><th>Status</th></tr>
          </thead>
          <tbody>
            {s.recent_orders.map((o) => (
              <tr key={o.id} className="border-t border-[#27272A]">
                <td className="py-2 font-mono">{o.order_id}</td>
                <td>{o.customer_name || "—"}</td>
                <td>{rupee(o.total)}</td>
                <td className="uppercase text-[#C5A059] text-xs tracking-widest">{o.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasPerm("manage_settings") && (
        <div className="admin-surface p-6 mt-6" data-testid="dashboard-delivery-card">
          <p className="eyebrow mb-4">Delivery Charges</p>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#8C867D] mb-1">Delivery Charge (₹)</label>
              <input type="number" value={ship.shipping_fee} onChange={(e) => setShip({ ...ship, shipping_fee: e.target.value })} className="admin-input w-full" data-testid="dashboard-shipping-fee" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#8C867D] mb-1">Free Delivery Above (₹)</label>
              <input type="number" value={ship.free_shipping_threshold} onChange={(e) => setShip({ ...ship, free_shipping_threshold: e.target.value })} className="admin-input w-full" data-testid="dashboard-free-threshold" />
            </div>
          </div>
          <p className="text-xs text-[#8C867D] mt-2">Orders below {rupee(ship.free_shipping_threshold)} are charged {rupee(ship.shipping_fee)}. Applies instantly to checkout.</p>
          <button onClick={saveShip} disabled={savingShip} className="mt-3 bg-[#C5A059] text-[#121212] px-6 py-2 text-xs uppercase tracking-widest" data-testid="dashboard-delivery-save">{savingShip ? "…" : "Save"}</button>
        </div>
      )}
    </div>
  );
}
